// MACHINERY V2 (Stroje) — Servisné správy: the status chips + sortable column
// header (M2.5).
//
// Split out of ServiceReportsTabV2.tsx for the 300-line cap. Reference impl:
// design_handoff_machinery_v3/machinery-profile-tabs.jsx (`PtFilterChip`,
// `SrHead`); artboard 1000.
//
// THE CHIP COUNTS ARE EXACT, NOT ESTIMATED. They are derived by the tab from the
// full result set — see the "status filtering is client-side" block in
// ServiceReportsTabV2.tsx for why every report is in hand.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../../shared/hooks/useTranslation";
import { ServiceReportStatusCode } from "../../../models/ServiceReportsModel";
import { SortGlyph, TabIntro, TabSearch, headCellStyle } from "./tabAtoms";

export const SrFilterChip: React.FC<{
  label: string;
  count: number;
  active: boolean;
  onClick: () => void;
}> = ({ label, count, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    style={{
      height: 28,
      padding: "0 11px",
      borderRadius: 999,
      cursor: "pointer",
      fontFamily: "inherit",
      fontSize: 12,
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      whiteSpace: "nowrap",
      background: active ? "var(--mod-accent)" : "var(--card)",
      border: "1px solid " + (active ? "var(--mod-accent)" : "var(--border)"),
      color: active ? "var(--mod-accent-fg)" : "var(--ink-2)",
      fontWeight: active ? 600 : 400,
    }}
  >
    {label}
    <span style={{ fontFamily: "var(--font-mono)", fontSize: 9.5, opacity: 0.75 }}>{count}</span>
  </button>
);

export const SrHeadCell: React.FC<{
  label: string;
  dir?: "asc" | "desc";
  align?: "right";
  onClick: () => void;
}> = ({ label, dir, align, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      ...headCellStyle,
      border: 0,
      background: "transparent",
      padding: 0,
      cursor: "pointer",
      justifyContent: align === "right" ? "flex-end" : "flex-start",
    }}
  >
    {label}
    <SortGlyph dir={dir} />
  </button>
);

export interface SrToolbarProps {
  search: string;
  onSearchChange: (v: string) => void;
  status: ServiceReportStatusCode | null;
  onStatusChange: (s: ServiceReportStatusCode | null) => void;
  counts: { all: number; inProgress: number; solved: number };
  visibleCount: number;
}

/** Search + the three status chips + the mono count (artboard 1000, top row). */
export const SrToolbar: React.FC<SrToolbarProps> = ({
  search,
  onSearchChange,
  status,
  onStatusChange,
  counts,
  visibleCount,
}) => {
  const { translate } = useTranslation("myMachinery");
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", minWidth: 0 }}>
      <TabSearch
        value={search}
        onChange={onSearchChange}
        placeholder={translate("myMachinery:searchReports")}
        clearLabel={translate("myMachinery:v2.tabs.clearSearch")}
      />
      <SrFilterChip
        label={translate("common:all")}
        count={counts.all}
        active={status === null}
        onClick={() => onStatusChange(null)}
      />
      <SrFilterChip
        label={translate("myMachinery:serviceReportStatus.inProgress")}
        count={counts.inProgress}
        active={status === ServiceReportStatusCode.inProgress}
        onClick={() => onStatusChange(ServiceReportStatusCode.inProgress)}
      />
      <SrFilterChip
        label={translate("myMachinery:serviceReportStatus.solved")}
        count={counts.solved}
        active={status === ServiceReportStatusCode.solved}
        onClick={() => onStatusChange(ServiceReportStatusCode.solved)}
      />
      <span style={{ flex: 1 }} />
      <TabIntro count={translate("myMachinery:v2.tabs.sr.count", { count: visibleCount })} />
    </div>
  );
};
